import { useEffect, useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Form, { Field as BaseField } from "rc-field-form";
import moment from "moment-timezone";
import { ProfileContext } from "../../context";
import DateInput from "../../../../components/Inputs/DateInput";
import CurrencySelect from "../../../../components/Inputs/CurrencySelect";
import CompensationSelect from "../../../../components/Inputs/CompensationSelect";
import ResourceSelect from "../../../../components/Inputs/ResourceSelect";
import MoneyInput from "../../../../components/Inputs/MoneyInput";
import Field from "../../../../components/Field";
import Button from "../../../../components/Button";
import {
	getSettingsResourceSearchFilters,
	renderSettingsResourceLabel,
} from "../../../../utils/helpers/settings";
import {
	getJobRates,
	renderCurrencySymbol,
} from "../../../../utils/helpers/job";
import { usePeopleApi } from "../../../../utils/api/people";
import { useProfileApi } from "../../../../utils/api/profile";
import { generateErrorsConfigForForm } from "../../../../utils/helpers/errors";
import { useCompany } from "../../../../utils/hooks/company";
import {
	applyTimezone,
	dateTimeParseZone,
	parseFloat2,
} from "../../../../utils/helpers/date";

function SecondaryJobForm({ close, values, mode }) {
	const { t } = useTranslation();
	const [form] = Form.useForm();
	const company = useCompany();
	const timezone = useMemo(
		() => company?.settings?.timezone?.value,
		[company?.settings?.timezone?.value],
	);
	const { user, submitting, submittingError, submit } =
		useContext(ProfileContext);

	const { changeJobsInformation: profileSubmit } = useProfileApi();
	const { changeJobsInformation: peopleSubmit } = usePeopleApi();

	const onFinish = useCallback(
		({ startDate, endDate, job, ...rest }) => { 
			if (!user) {
				return;
			}

			const secondaryJob = {
				primary: false,
				jobId: job.id,
				code: job.code,
				description: job.description,
				pbjCode: job.pbjCode,
				displayName: renderSettingsResourceLabel(job),
				endDate: endDate ? applyTimezone(endDate, timezone, true) : undefined,
				startDate:
					startDate && applyTimezone(startDate, timezone, true),
				currency: rest.currency,
				...getJobRates({
					hourlyOrSalaried: rest.hourlyOrSalaried,
					salary: Number(rest?.salary || 0) ,
					hourlyRate: Number(rest?.hourlyRate || 0),
				}),
				otherRate1: parseFloat2(rest?.otherRate1 || '0.00'),
				otherRate2: parseFloat2(rest?.otherRate2 || '0.00'),
				otherRate3: parseFloat2(rest?.otherRate3 || '0.00'),
				otherRate4: parseFloat2(rest?.otherRate4 || '0.00'),
			};

			if (mode === "edit") {
				secondaryJob.id = values?.id;
			}

			submit(profileSubmit, peopleSubmit, secondaryJob, close);
		},
		[
			timezone,
			close,
			mode,
			user,
			values,
			profileSubmit,
			submit,
			peopleSubmit,
		],
	);

	const onlyView = mode === "view";

	useEffect(() => {
		form.setFieldsValue({
			job: mode !== "clone" && {
				id: values?.jobId,
				code: values?.code,
				description: values?.description,
				pbjCode: values?.pbjCode,
			},
			startDate:
				values?.startDate &&
				dateTimeParseZone(values?.startDate, "date"),
			hourlyOrSalaried: values?.hourlyOrSalaried || "H",
			currency: values?.currency,
			hourlyRate: parseFloat2(values?.hourlyRate || '0.00'),
			otherRate1: parseFloat2(values?.otherRate1 || '0.00'),
			otherRate2: parseFloat2(values?.otherRate2 || '0.00'),
			otherRate3: parseFloat2(values?.otherRate3 || '0.00'),
			otherRate4: parseFloat2(values?.otherRate4 || '0.00'),
			salary: parseFloat2(values?.salary),
			endDate:
				values?.endDate && dateTimeParseZone(values?.endDate, "date"),
		});
	}, [form, values, mode]);

	useEffect(() => {
		const fieldErrors = generateErrorsConfigForForm(
			[
				"startDate",
				"hourlyOrSalaried",
				"currency",
				"hourlyRate",
				"otherRate1",
				"otherRate2",
				"otherRate3",
				"otherRate4",
				"job",
				"salary",
			],
			submittingError,
		);
		form.setFields(fieldErrors);
	}, [form, submittingError]);

	return (
		<Form
			form={form}
			layout="vertical"
			onFinish={onFinish}
			scrollToFirstError
		>
			<Field
				name="startDate"
				label={t("effective-date")}
				rules={[
					{
						required: true,
						message: t("required-effective-date"),
					},
				]}
				className="d-flex flex-column"
			>
				<DateInput 
					placeholderText={t("effective-date")} 
					disabled={onlyView}
				/>
			</Field>

			<Field
				name="job"
				label={t("job")}
				rules={[
					{
						required: true,
						message: t("required-job"),
					},
				]}
			>
				<ResourceSelect
					labelPropName="description"
					resourcePath="/jobs"
					renderLabel={renderSettingsResourceLabel}
					placeholder={t("job")}
					hasSearch
					getSearchFilters={(search) => getSettingsResourceSearchFilters(search, ["id", "code", "description", "pbjCode"])}
					disabled={onlyView || mode === "edit"}
				/>
			</Field>

			<Field
				name="hourlyOrSalaried"
				label={t("hourly-or-salary")}
				rules={[
					{
						required: true,
						message: t("required-hourly-or-salary"),
					},
				]}
			>
				<CompensationSelect
					placeholder={t("hourly-or-salary")}
					disabled={onlyView}
				/>
			</Field>

			<Field
				name="currency"
				label={t("pay-currency")}
				rules={[
					{
						required: true,
						message: t("required-pay-currency"),
					},
				]}
			>
				<CurrencySelect
					placeholder={t("pay-currency")}
					disabled={onlyView}
				/>
			</Field>

			<BaseField shouldUpdate noStyle>
				{({ }, { }, { getFieldValue }) => {
					const compensation = getFieldValue("hourlyOrSalaried");

					switch (compensation) {
						case "H": {
							return (
								<>
									<Field
										name="hourlyRate"
										label={t("hourly-rate")}
										rules={[
											{
												required: true,
												message: t(
													"required-hourly-rate"
												),
											},
										]}
									>
										<MoneyInput
											prefix={renderCurrencySymbol(
												getFieldValue("currency"),
											)}
											className="form-control"
											placeholder={t("hourly-rate")}
											disabled={onlyView}
										/>
									</Field>
									<Field
										name="otherRate1"
										label={t("other-rate1")}
									>
										<MoneyInput
											prefix={renderCurrencySymbol(
												getFieldValue("currency"),
											)}
											className="form-control"
											placeholder={t("other-rate1")}
											disabled={onlyView}
										/>
									</Field>
									<Field
										name="otherRate2"
										label={t("other-rate2")}
									>
										<MoneyInput
											prefix={renderCurrencySymbol(
												getFieldValue("currency"),
											)}
											className="form-control"
											placeholder={t("other-rate2")}
											disabled={onlyView}
										/>
									</Field>
									<Field
										name="otherRate3"
										label={t("other-rate3")}
									>
										<MoneyInput
											prefix={renderCurrencySymbol(
												getFieldValue("currency"),
											)}
											className="form-control"
											placeholder={t("other-rate3")}
											disabled={onlyView}
										/>
									</Field>
									<Field
										name="otherRate4"
										label={t("other-rate4")}
									>
										<MoneyInput
											prefix={renderCurrencySymbol(
												getFieldValue("currency"),
											)}
											className="form-control"
											placeholder={t("other-rate4")}
											disabled={onlyView}
										/>
									</Field>
								</>
							);
						}
						default: {
							return (
								<Field
									name="salary"
									label={t("salary")}
									rules={[
										{
											required: true,
											message: t("required-salary"),
										},
									]}
								>
									<MoneyInput
										prefix={renderCurrencySymbol(
											getFieldValue("currency"),
										)}
										className="form-control"
										placeholder={t("salary")}
										disabled={onlyView}
									/>
								</Field>
							);
						}
					}
				}}
			</BaseField>

			<Field 
				name="endDate" 
				label={t("end-date")} 
				className="d-flex flex-column"
				rules={[
					({ getFieldValue }) => ({
						validator: (_, value) => {
							const startDate = getFieldValue("startDate");
							if (startDate && value && moment(value).isSameOrBefore(moment(startDate), "date")) {
								return Promise.reject(new Error(t("end-date-bigger-than-start-date")))
							}
							return Promise.resolve();
						}
					})
				]}
			>
				<DateInput disabled={onlyView} placeholderText={t("end-date")} />
			</Field>

			<div className="d-flex justify-content-end">
				<Button
					onClick={close}
					disabled={submitting}
					className="btn-round btn-icon shadow-none border btn btn-secondary btn-sm"
				>
					{t("cancel")}
				</Button>

				{!onlyView && (
					<Button
						color="primary"
						type="submit"
						className="btn-round btn-icon shadow-none border px-3 btn btn-dark btn-sm"
					>
						{t("save")}
					</Button>
				)} 
			</div>
		</Form>
	);
}

export default SecondaryJobForm;
